/**
 * 担当案件テーブル（開発 view）の「アンケート」列セル。
 *
 * テーブルが**母票ベース**（1 行 = 案件 1 件）で、DLE 評価も母票 1 件 = 1 通なので、
 * 1 セルが担当するアンケートは**最大 1 通**。件数バッジも内訳 tooltip も要らず、
 * 状態がそのまま出る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 担当案件テーブル（開発 view）のアンケート列。母票 1 件ぶんの回答状態とモーダル起動を担う（回答ボタンはライト青 / ダーク黄）
 * @graph-connects DleSurveyModal [calls] 回答モーダル表示
 */
import { useState } from 'react';

import { Badge } from '@cortex/web-design-system/components/ui/Badge';

import { DleSurveyModal } from './DleSurveyModal.js';
import type { DleEvaluationTask } from '../../../types/dle-evaluation.js';

interface DleSurveyCellProps {
  /** この母票のアンケート。対象ステータス前 / DLE 未解決なら null */
  task: DleEvaluationTask | null;
}

/** 回答済みの tooltip 文言（回答者と回答日）。 @graph-connects none */
function answeredTitle(task: DleEvaluationTask): string {
  const at = task.respondedAt ? task.respondedAt.slice(0, 10) : '-';
  return `${task.respondedBy ?? '-'} が ${at} に回答`;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business アンケート列セル。未回答なら回答ボタン、回答済みならバッジ、対象外は理由付きの「—」
 * @graph-connects DleSurveyModal [calls] 回答モーダル表示
 */
export function DleSurveyCell({ task }: DleSurveyCellProps) {
  const [open, setOpen] = useState(false);

  // 評価対象になっていない母票（開発完了より前のステータス / DLE が解決できない）。
  // 理由は API から個別には返さないため、ここでは「対象になる条件」だけを示す。
  if (task === null) {
    return (
      <span
        className="text-[var(--color-text-muted)]"
        title="開発完了・結合テスト完了・総合テスト完了・完了 のいずれかになり、担当 DLE が決まっている案件が対象です"
      >
        —
      </span>
    );
  }

  if (task.state === 'answered') {
    return (
      <Badge variant="default" size="sm" title={answeredTitle(task)}>
        回答済
      </Badge>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
        }}
        /*
          開発 view 専用の行なので、色も view のアクセントに合わせる（DevDesignViewSwitch の
          アクティブ側と同じトークン = ライト青 / ダーク黄）。Tailwind の `blue-*` を直に書くと
          ダークでも青のままになり、view の色とボタンの色がずれる。
        */
        className="rounded-full bg-[var(--color-dev-accent)] px-3 py-0.5 text-xs font-medium whitespace-nowrap text-[var(--color-dev-accent-on)] transition-opacity hover:opacity-80"
      >
        回答する
      </button>
      <DleSurveyModal
        tasks={[task]}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      />
    </>
  );
}
